/* eslint-disable */
import type { LocalizableString } from "../cash/localization/localization.pb";
import type { Animation } from "../cash/ui/Animation.pb";
import type { Color } from "../cash/ui/Color.pb";
import type { Image } from "../cash/ui/Image.pb";
import type { Country } from "../common/countries.pb";
import type { Money } from "../common/currency.pb";
import type { CurrencyCode } from "../common/currency_codes.pb";
import type { GlobalAddress } from "../common/location.pb";
import type { BlockerAction } from "./blocker_action.pb";
import type { CardCustomizationData } from "./cards.pb";
import type { Avatar } from "./ui.pb";

/**
 * Describes a screen of stacked vertical content elements, potentially with one or two additional
 * fixed navigation buttons at the top or bottom.
 *
 * # Layout
 *
 * ## Spacing
 *
 * Elements are displayed in a vertical stack, in the order specified by the `elements` field.
 * The spacing between adjacent elements is hardcoded into the clients, but the server can specify
 * `SpacerElement`s in order to tailor the vertical alignment of content on the screen. A
 * `SpacerElement` is flexible in height, and consumes all available space. If multiple
 * `SpacerElement`s are specified then they each consume some portion of the remaining vertical
 * space, with the division specified by the `proportion` field.
 *
 * For example, to create vertically centered content the server could specify:
 *
 * ```
 * [SpacerElement(proportion=1), TextElement(size=MEDIUM), SpacerElement(proportion=1)]
 * ```
 *
 * To create a screen with top-aligned content and a footer:
 *
 * ```
 * [TextElement(size=MEDIUM), SpacerElement(proportional=1), TextElement(size=SMALL)]
 * ```
 *
 * ## Edge Cases
 *
 * Certain edge cases may come up for clients and should be handled as follows:
 *
 * If the client calculates that more content exists than can fit on the screen, then the screen
 * should scroll to expose the additional content. In this case any `SpacerElement`s should be
 * removed client-side and the standard hardcoded client spacing should be used.
 *
 * If no `SpacerElement` is included from the server, then an implicit `SpacerElement` should be
 * assumed at both the top and bottom of the content, thus making the content be centered.
 */
export interface FormBlocker {
  /** The content elements to display, in an ordered vertical stack. */
  elements?: FormBlockerElement[];
  /**
   * The primary action for navigation, displayed as a button at either the bottom or top of
   * the screen.
   *
   * If both a primary and secondary action are specified then the primary action button is
   * displayed on the right. If only one is specified then the action button is centered.
   */
  primary_action?: BlockerAction;
  /**
   * The secondary action for navigation, displayed as a button at either the bottom or top of
   * the screen.
   *
   * If both a primary and secondary action are specified then the secondary action button is
   * displayed on the left. If only one is specified then the action button is centered.
   */
  secondary_action?: BlockerAction;
  on_display_effect?: FormBlockerOnDisplayEffect | undefined;
  remote_on_display_effect?:
    | FormBlockerRemoteOnDisplayEffect
    | undefined;
  /**
   * True if the customer must scroll to the bottom to proceed. This will have no effect if the
   * content fits on the screen and is not scrollable.
   */
  requires_full_scroll?: boolean;
  /**
   * Style to be applied to the `secondary_action` field. If absent, `SECONDARY` will be
   * used.
   */
  secondary_action_style?: FormBlockerElementButtonElementStyle;
  /** The help action for displaying a help icon "?" button on the top of the screen. */
  help_action?: BlockerAction;
}

export enum FormBlockerOnDisplayEffect {
  NONE = "NONE",
  CONFETTI = "CONFETTI",
}

/**
 * Represents a single vertical content element in the form.
 * Only one of the nested element fields will ever be set.
 */
export interface FormBlockerElement {
  /** An identifier to uniquely identify this element within the given form. */
  id?: string;
  /** Display elements. */
  copyable_element_group?: FormBlockerElementCopyableElementGroup | undefined;
  card_element?: FormBlockerElementCardElement | undefined;
  gift_card_element?: FormBlockerElementGiftCardElement | undefined;
  hero_element?: FormBlockerElementHeroElement | undefined;
  avatar_element?: FormBlockerElementAvatarElement | undefined;
  customized_card_element?: FormBlockerElementCustomizedCardElement | undefined;
  date_input_element?: FormBlockerElementDateInputElement | undefined;
  local_image_element?: FormBlockerElementLocalImageElement | undefined;
  money_element?: FormBlockerElementMoneyElement | undefined;
  text_element?: FormBlockerElementTextElement | undefined;
  remote_image_element?: FormBlockerElementRemoteImageElement | undefined;
  timeline_element?: FormBlockerElementTimelineElement | undefined;
  legal_text_element?: FormBlockerElementLegalTextElement | undefined;
  call_to_action_element?: FormBlockerElementCallToActionElement | undefined;
  captioned_tile_element?: FormBlockerElementCaptionedTileElement | undefined;
  notice_element?: FormBlockerElementNoticeElement | undefined;
  family_upsell_element?: FormBlockerElementFamilyUpsellElement | undefined;
  merchant_transaction_element?: FormBlockerElementMerchantTransactionElement | undefined;
  payment_plan_summary_element?: FormBlockerElementPaymentPlanSummaryElement | undefined;
  payment_plan_schedule_element?: FormBlockerElementPaymentPlanScheduleElement | undefined;
  upsell_element?:
    | FormBlockerElementUpsellElement
    | undefined;
  /** Input elements. */
  address_element?: FormBlockerElementAddressElement | undefined;
  button_element?: FormBlockerElementButtonElement | undefined;
  cashtag_element?: FormBlockerElementCashtagElement | undefined;
  option_picker_element?: FormBlockerElementOptionPickerElement | undefined;
  text_input_element?: FormBlockerElementTextInputElement | undefined;
  multiline_text_input_element?: FormBlockerElementMultilineTextInputElement | undefined;
  money_input_element?: FormBlockerElementMoneyInputElement | undefined;
  emoji_picker_element?: FormBlockerElementEmojiPickerElement | undefined;
  merchant_transaction_picker_element?: FormBlockerElementMerchantTransactionOptionPicker | undefined;
  location_element?:
    | FormBlockerElementLocationElement
    | undefined;
  /** Layout elements. */
  divider_element?: FormBlockerElementDividerElement | undefined;
  spacer_element?:
    | FormBlockerElementSpacerElement
    | undefined;
  /** List view elements. */
  detail_row_element?: FormBlockerElementDetailRowElement | undefined;
  selectable_row_element?: FormBlockerElementSelectableRowElement | undefined;
}

/** A field to collect an address */
export interface FormBlockerElementAddressElement {
  /** The initial value with which to populate the address fields. Optional. */
  prefill_address?: GlobalAddress;
  /** The country where the expected address is located. */
  country_code?: Country;
  /**
   * If true, then show autocomplete results as the customer types.
   * If false, then show discrete fields for each aspect of the address, such as
   * state, ZIP, etc.
   */
  autocomplete?: boolean;
}

/**
 * An element to display one or more avatars for entities (Customers, Merchants, etc) in the Cash system.
 *
 * An avatar can be displayed either as a couple of characters (`monogram_text`) on a colored background
 * (`background_color`) or as a photo downloaded from the server (`photo_url`), or, if neither `photo_url`
 * nor `monogram_text` is specified, a generic customer icon will be shown on a colored background.
 *
 * If a photo is specified then the monogram/accent color version will be shown as a placeholder while the
 * photo is downloaded and loaded.
 */
export interface FormBlockerElementAvatarElement {
  /** If more than 1 avatar is provided, they will be diagonally stacked. */
  avatars?: FormBlockerElementAvatarElementAvatar[];
  /** An optional badge to display in the lower right corner of the image. Available in client versions >= 3.46. */
  badge?: FormBlockerElementAvatarElementBadge;
  /** The background color to show with the badge, if provided. Available in client versions >= 3.46. */
  badge_background_color?: Color;
}

/**
 * A glyph that may be shown stacked in the lower right corner of the element.
 * Available in client versions >= 3.46.
 */
export enum FormBlockerElementAvatarElementBadge {
  /** CHECKMARK - Show a checkmark icon */
  CHECKMARK = "CHECKMARK",
  /** PAYMENT_PLAN - Shows the payment plan split icon */
  PAYMENT_PLAN = "PAYMENT_PLAN",
}

export interface FormBlockerElementAvatarElementAvatar {
  /**
   * The accent color to use as the background of the avatar if no photo is present.
   * 6 digit hex with leading # sign (ex: "#BBBFB3").
   * If not specified, the client will fall back to using the Gray 85 color, #D9D9D9.
   * Optional.
   *
   * Deprecated. Use `background_color` instead.
   *
   * @deprecated
   */
  accent_color?: string;
  /**
   * The accent color to use as the background of the avatar if no photo is present.
   * If not specified, the client will fall back to using the Gray 85 color, #D9D9D9.
   * Optional.
   */
  background_color?: Color;
  /**
   * The monogram text to display in the middle of the avatar if no photo is present.
   * Currently only one character is allowed.
   * Optional.
   */
  monogram_text?: string;
  /**
   * The URL for the photo to display in the avatar, in place of a background color and monogram.
   * Optional.
   *
   * Deprecated. Use `image` instead.
   *
   * @deprecated
   */
  photo_url?: string;
  /**
   * The accessibility value to apply on the client.
   * Most likely the display name for the entity that the avatar represents.
   * Optional.
   */
  accessibility_value?: string;
  localizable_accessibility_value?: LocalizableString;
  /**
   * Light- and dark-themed URLs for the photo to display in the avatar, in place of a background color and monogram.
   * Optional.
   */
  image?: Image;
  /**
   * True if `image` is a template image and should be colorized/tinted.
   * `background_color` will also be shown when this is true.
   * Available in client versions >= 3.53.
   */
  is_template_avatar?: boolean;
  /** The remote badge image, available only to clients on version 3.56 and above. */
  badge_image?: Image;
  badge_shape?: FormBlockerElementAvatarElementAvatarBadgeShape;
}

export enum FormBlockerElementAvatarElementAvatarBadgeShape {
  /** CIRCULAR - Circular shape, which is the default. */
  CIRCULAR = "CIRCULAR",
  /** CARD - A card shape, used for gift cards. */
  CARD = "CARD",
}

/** A button which performs an action. */
export interface FormBlockerElementButtonElement {
  action?: BlockerAction;
  /** The style with which to display the button. */
  style?: FormBlockerElementButtonElementStyle;
}

export enum FormBlockerElementButtonElementStyle {
  /** PRIMARY - Appropriate for the main actions the customer may select. */
  PRIMARY = "PRIMARY",
  /**
   * SECONDARY - Appropriate for auxiliary actions that a customer can take outside the
   * scope of the blocker's main purpose.
   */
  SECONDARY = "SECONDARY",
  /**
   * TERTIARY - Appropriate for yet less important actions that a customer can take outside the
   * scope of the blocker's main purpose.
   */
  TERTIARY = "TERTIARY",
}

/**
 * An text field which allows the customer to select their Cashtag.
 * This element should provide real-time feedback to the customer as they type
 * which indicates whether or not the currently entered Cashtag is available.
 * This availability information can be fetched from `get-cashtag-status`.
 */
export interface FormBlockerElementCashtagElement {
  /**
   * The symbol to always show as a prefix to the entered Cashtag. For the US this is "$".
   * Although this should almost always be a single character, clients should support an
   * empty prefix or multiple characters.
   */
  prefix_symbol?: string;
  /**
   * If non-null, this should be used as light grey hint text for the entry
   * field, which disappears once the user starts typing.
   */
  hint_text?: string;
  /**
   * A suggested Cashtag for the customer. May be null if no good suggestion is available.
   * This value should not include the prefix symbol, such as "$". If non-null, this value
   * should be used to pre-populate the entry field.
   */
  prefill_text?: string;
}

/** An element for collecting the day, month, and year of a date. */
export interface FormBlockerElementDateInputElement {
  /**
   * The earliest possible date that can be entered in the field.
   * This value is inclusive, and the minimum date is itself considered a valid date.
   *
   * The minimum date is specified as an ISO 8601 string and therefore includes a
   * time and time zone, which should be considered when specifying the value. If
   * any part of a given day is on or after the minimum date as then it is considered valid.
   * The implementation for this be roughly equivalent to:
   *   - Create a date object with the entered date at 23:59 in the local time zone
   *   - Ensure that the created date is after the minimum date.
   *
   * If a value is entered that falls before the minimum date, or a partial date is entered
   * that cannot fall on or after the minimum date, then the specific field being edited
   * (such as the year field) should shake to indicate that the attempted entry is invalid.
   *
   * This value is optional. If not specified then Jan 1, 1900 in the local time zone
   * will be assumed to be the minimum date.
   */
  minimum_date?: string;
  /**
   * The latest possible date that can be entered in the field.
   * This value is inclusive, and the maximum date is itself considered a valid date.
   *
   * The maximum date is specified as an ISO 8601 string and therefore includes a
   * time and time zone, which should be considered when specifying the value. If
   * any part of a given day is on or before the maximum date then it is considered valid.
   * The implementation for this be roughly equivalent to:
   *   - Create a date object with the entered date at 00:00 in the local time zone
   *   - Ensure that the created date is before the maximum date.
   *
   * If a value is entered that falls after the maximum date, or a partial date is entered
   * that cannot fall on or before the maximum date, then the specific field being edited
   * (such as the year field) should shake to indicate that the attempted entry is invalid.
   *
   * This value is optional. If not specified then Dec 31, 2099 in the local time zone
   * will be assumed to be the maximum date.
   */
  maximum_date?: string;
  /** The accessibility label for this element. */
  accessibility_text?: string;
  /**
   * If true, the entry fields should allow empty values.
   * If not set, default to false for backwards compatibility.
   * Available in client versions >= 3.54.
   */
  is_optional?: boolean;
  /**
   * The default value to use for the input field. This value is optional, and if omitted
   * no default should be set.
   */
  default_value?: string;
}

/**
 * A divider line between two other elements. This line is displayed in gray and spans
 * the full width of the screen, defaults to a HAIRLINE divider.
 */
export interface FormBlockerElementDividerElement {
  /** The style that should be applied to the divider line by te client. */
  style?: FormBlockerElementDividerElementDividerStyle;
}

/** The style that should be applied to the divider line. */
export enum FormBlockerElementDividerElementDividerStyle {
  /** HAIRLINE - A hairline-thick divider line as narrow as the client can display. */
  HAIRLINE = "HAIRLINE",
  /** ELEMENT - A medium-sized divider, less prominent than a SECTION divider. */
  ELEMENT = "ELEMENT",
  /** SECTION - A larger divider, used to separate sections from each-other. */
  SECTION = "SECTION",
}

/**
 * A field to collect generic text input. This is a highly flexible field which allows for
 * realtime validation of user input and realtime formatting of the output while it is being
 * typed.
 *
 * Some of the sample inputs which can be collected and validated using this field include
 * Name, SSN, email, password, and confirmation code. Some input types, such as phone numbers
 * or credit cards require additional UI and complexity which warrant their own element types.
 *
 * To model an SSN input, you would specify something like:
 *   validation = Validation()
 *   validation.regex = "\d+"
 *   validation.template.template = "XXX-XXX-XXX"
 *   validation.template.template_placeholder_character = "X"
 *
 *   inputField = InputField()
 *   inputField.keyboardType = NUMBER_PAD
 *   inputField.hint_text = "SSN"
 *   inputField.validations.add(validation)
 *
 *   inputElement = TextInputElement()
 *   inputElement.inputFields.add(inputField)
 */
export interface FormBlockerElementTextInputElement {
  /**
   * The text input fields to display to the user. These should be displayed as
   * a vertical stack if more than one is present.
   */
  input_fields?: FormBlockerElementTextInputElementInputField[];
  /**
   * Optional. Supplementary left-aligned text displayed below input fields.
   * Rendered in SMALL font with a lock glyph to the left of the text.
   */
  security_text?: string;
}

/**
 * The type of keyboard to display for a given field.
 * If a client does not support a given keyboard type then it is responsible for falling
 * back to another type which provides a superset of the specified keyboard's functionality.
 *
 * For reference:
 *   Android keyboard types: https://developer.android.com/reference/android/text/InputType
 *   iOS keyboard types: https://developer.apple.com/documentation/uikit/uikeyboardtype
 */
export enum FormBlockerElementTextInputElementKeyboardType {
  /** DEFAULT - A standard full keyboard, such as the QWERTY keyboard for English countries. */
  DEFAULT = "DEFAULT",
  /** NUMBER_PAD - A numbers-only keyboard which includes only 0-9 and the backspace key. */
  NUMBER_PAD = "NUMBER_PAD",
  /** NUMBER_PAD_WITH_DECIMAL - A numbers keyboard that also includes a decimal (.) key. */
  NUMBER_PAD_WITH_DECIMAL = "NUMBER_PAD_WITH_DECIMAL",
  /** NUMBER_PAD_WITH_ABC - A numbers keyboard that also allows switching to a full keyboard. */
  NUMBER_PAD_WITH_ABC = "NUMBER_PAD_WITH_ABC",
  /**
   * EMAIL - A keyboard with a layout optimized for entry of email addresses. If a client does not
   * support such a keyboard then the client should fall back to the `DEFAULT` keyboard.
   */
  EMAIL = "EMAIL",
}

/** The display security for the text in the field. */
export enum FormBlockerElementTextInputElementSecurity {
  /** CLEAR_TEXT - Text is fully displayed. */
  CLEAR_TEXT = "CLEAR_TEXT",
  /** SECURE - Text is obfuscated as it's typed, like a standard password field. */
  SECURE = "SECURE",
}

/**
 * Defines what characters can be entered into the field and does realtime formatting
 * to insert characters that help with readability.
 *
 * For example, given a regex of "\d+" and a template of "XXX-XXX", the user's input
 * of "123ab456" would be displayed as "123-456". The letters would be rejected and
 * ignored because they don't pass the regex, and the dash would be inserted because
 * of the template.
 */
export interface FormBlockerElementTextInputElementValidation {
  /**
   * A regular expression defining what a valid user-entered value looks like.
   * This expression should match a full and complete value that is ready to
   * be submitted to the server.
   * *
   * If text entered by the user fully, or *partially* (as a prefix) matches
   * this regex, then the accompanying template will be applied to the text as
   * the user types.
   *
   * Prefix matching in this case means that the current input
   * still has the potential to match the regex by appending additional characters.
   * For example, with the regex "\d{3}", the inputs "1", "12", and "123" are all
   * potential matches, but the inputs "a", "1a", and "1234" are not.
   *
   * The regex defines valid characters input by the user, and if an accompanying
   * template is being used then the regex should not include any format characters
   * inserted by the template. For example, the regex for a valid US phone
   * number would be "\d{10}" rather than something like "(\d{3}) \d{3}-\d{4}".
   * Clients should strip the template's formatting characters before attempting to
   * apply the regex.
   *
   * A validation's template implicitly specifies the required length of a user's
   * input by specifying the number of token characters which must be filled. This
   * has the potential to conflict with the length specified by the regex (which
   * would be a bug). To avoid this, it's recommended that if a template is being
   * used then no length limitation is specified in the regex.
   *
   * Some common regex values include:
   *   "\d+"     - User can only enter numbers. Combine with a template to limit
   *               length.
   *
   *   "\d{3}"   - User can only enter three numbers. Useful if there is no template
   *               to limit length.
   *
   *   ".{3,20}" - User must enter at least three characters and no more than 20.
   *               Any character is allowed.
   */
  regex?: string;
  /**
   * The template used to specify where formatting characters should be inserted.
   * If no formatting characters are needed then this field can be omitted.
   */
  template?: FormBlockerElementTextInputElementTemplate;
}

/**
 * A specification for how to format a given string, such as:
 *  "123456" (input) + "XXX-XXX" (template) -> "123-456"
 *
 * A Swift implementation for this logic is available here:
 * https://github.com/square/StringTemplate
 */
export interface FormBlockerElementTextInputElementTemplate {
  /**
   * A template string which will be applied to the user's text as it is
   * typed. For example, a template of "XXX-XXX-XXX" would automatically insert
   * a dash after the user has typed the third character, and again after
   * the sixth.
   */
  template?: string;
  /**
   * The character in the template string which should be replaced (one or more
   * times) in the template string with characters entered by the user.
   *
   * This must be a single character. In the example above, "X" is used.
   */
  template_placeholder_character?: string;
  /**
   * Characters that should be treated like user-entered characters even if
   * they may have been inserted as part of the template. The string value in
   * this case is treated as an unordered set of characters.
   *
   * This should only be needed in cases where the template may automatically
   * insert a character which the user can also enter and usually should not
   * be required.
   *
   * As an example, a UK phone number is prefixed by a zero, but the user may
   * also need to enter a zero later in the phone number, like "07 502332".
   * The template for this would look like "0X XXXXXX".
   *
   * Without special casing for "0" in this case, the template would be applied
   * to the input "7502332" like so:
   *  - Strip format characters ("0" and " ") -> "752332" (no zeros)
   *  - Apply template: "07 52332" (note missing second zero)
   *
   * By specifying the value for this field as "0" the template will preserve
   * the "0" in the input string and create the correct result.
   */
  user_insertable_format_characters?: string;
}

/**
 * A single field within the text input area. The text input element may
 * have multiple vertically-stacked input fields if needed.
 */
export interface FormBlockerElementTextInputElementInputField {
  /**
   * If non-null, this should be used as light grey hint text for the entry
   * field, which disappears once the user starts typing.
   */
  hint_text?: string;
  /** If non-null, this should be used to pre-populate the entry field. */
  prefill_text?: string;
  /**
   * The type of keyboard to display. If null then the `DEFAULT` keyboard
   * should be used.
   */
  keyboardType?: FormBlockerElementTextInputElementKeyboardType;
  /** The level of security used when displaying the text. */
  security?: FormBlockerElementTextInputElementSecurity;
  /**
   * The validation and formatting to use when text is being entered.
   *
   * Note that this is an ordered, repeated field. If multiple validation entries
   * exist then the one used will be the first one that includes a regex where the
   * current text matches, or could still potentially match after adding additional
   * characters.
   *
   * For example, to support either a five-digit or nine-digit postal code, these
   * two regex/template pairs could be used:
   *   "\d{5}", "XXXXX"
   *   "\d{9}", "XXXXX-XXXX"
   *
   * After the user types the sixth number the first regex no longer matches, nor
   * has the potential to match by typing more characters at the end of the input.
   * Given this, the formatting for the second would be used.
   */
  validations?: FormBlockerElementTextInputElementValidation[];
  /**
   * true indicates that the entry field supports multiple lines of text.
   * false or null indicate that only single line input is supported.
   *
   * Deprecated by MultilineTextInputElement.
   *
   * @deprecated
   */
  multiline?: boolean;
  /** An optional accessory view to display for SECURE fields; ignored for non-SECURE fields. */
  secure_field_accessory?: FormBlockerElementTextInputElementInputFieldSecureFieldAccessory;
}

/** An accessory view to display for SECURE fields. Optional. */
export enum FormBlockerElementTextInputElementInputFieldSecureFieldAccessory {
  /** NONE - No accessory. */
  NONE = "NONE",
  /**
   * LOCK - A lock glyph displayed to the right of the field.
   * Nothing happens if the glyph is tapped.
   */
  LOCK = "LOCK",
  /**
   * SHOW_HIDE - A show/hide glyph displayed to the right of the field,
   * which either obfuscates or displays the input string when tapped.
   */
  SHOW_HIDE = "SHOW_HIDE",
}

/**
 * A text field that supports multiline should start as a single line and
 * expand vertically to accommodate additional content as the user types.
 * If content is deleted it should contract back so that only as much vertical
 * space is used as is needed.
 */
export interface FormBlockerElementMultilineTextInputElement {
  /**
   * If non-null, this should be used as light grey hint text for the entry
   * field, which disappears once the user starts typing.
   */
  hint_text?: string;
  /** If non-null, this should be used to pre-populate the entry field. */
  prefill_text?: string;
  /**
   * If non-null, this is the maximum number of UTF-16 characters that this entry field should
   * allow. null means there is no limitation on the number of characters.
   */
  max_character_count?: number;
  /**
   * If true, the entry field should not allow entering newline characters. Any newline
   * characters in pasted text should be converted to spaces. It is an error to submit input
   * containing newlines when this field is set to true.
   */
  disallow_newlines?: boolean;
  /**
   * If true, the entry field should collapse sequences of whitespace characters into one space
   * character. Any whitespace in pasted text should be collapsed as well. It is an error to
   * submit input containing sequences of whitespace characters when this field is set to true.
   */
  collapse_whitespace?: boolean;
  /**
   * If true, the entry field should not allow non-empty text.
   * If not set, default to false for backwards compatibility.
   * Available in client versions >= 3.63.
   */
  is_required?: boolean;
}

/**
 * A field to collect money amount input. This is an input field which allows for
 * money input specifically with support for realtime validation and denomination settings.
 */
export interface FormBlockerElementMoneyInputElement {
  /** The currency the money amount should be in, which is used to display the currency sign. */
  currency_code?: CurrencyCode;
  /**
   * If non-null, this should be used as light grey hint text for the entry
   * field, which disappears once the user starts typing.
   */
  hint_text?: string;
  /** If non-null, this should be used to pre-populate the entry field. */
  prefill_amount?: Money;
  /** If non-null, this is the minimum amount (inclusive) used to validate against the entered amount */
  minimum_amount?: Money;
  /** If non-null, this is the maximum amount (inclusive) used to validate against the entered amount */
  maximum_amount?: Money;
  /** If true, the entry fields should allow empty values. */
  is_optional?: boolean;
}

/** An element used to allow users to pick an option out of a static list of options. */
export interface FormBlockerElementOptionPickerElement {
  /**
   * The options that the customer can select, where the value of the option is actually
   * displayed to the user.  This will be an ordered list, and the client should maintain that
   * order when presenting.
   */
  options?: FormBlockerElementOptionPickerElementOption[];
  /**
   * The index of the option to show as initially selected.
   * If this property is not set then no option should be selected by default. Clients should
   * be careful to differentiate between "unset" and reading the default value of zero.
   */
  selected_option?: number;
  /**
   * If true, customer can submit the form without selecting any option.
   * If not set, default to false for backwards compatibility.
   * Available in client versions >= 3.59.
   */
  is_optional?: boolean;
}

export interface FormBlockerElementOptionPickerElementOption {
  /**
   * A machine-read identifier to uniquely identify this option within the list of options.
   * This will be the same for everyone choosing this option regardless of the value it was
   * actually displayed with.
   */
  id?: string;
  /**
   * A human-readable string that is displayed to the customer. The value might change based
   * on region or language.
   */
  value?: string;
  /** A human-readable string that is displayed to the customer, below the `value`. */
  subtitle?: string;
  /** The default case. */
  selectable?: FormBlockerElementOptionPickerElementOptionSelectable | undefined;
  unselectable?:
    | FormBlockerElementOptionPickerElementOptionUnselectable
    | undefined;
  /**
   * Color of subtitle. If nil, subtitle is displayed in default color.
   * Overrides color set based on selectability property. Available in iOS 3.61
   */
  subtitle_color?: FormBlockerElementOptionPickerElementOptionTextColor;
}

export enum FormBlockerElementOptionPickerElementOptionTextColor {
  DEFAULT = "DEFAULT",
  /** WARNING - Red */
  WARNING = "WARNING",
}

export interface FormBlockerElementOptionPickerElementOptionConfirmationDialog {
  /** A required short title summarizing the action being confirmed, such as “Confirm Deletion”. This should be in title case. */
  title?: string;
  /**
   * A required message with details about the action being confirmed, such as “This will permanently delete Oscar’s phone number.
   * This should use complete sentences and be in sentence (normal) case.
   */
  message?: string;
  /**
   * The text of button used to confirm the action. This should be as descriptive of the action as possible, such as “Delete”.
   * If nothing is sufficiently short then use “OK”. This field is required.
   */
  confirm_button_text?: string;
  /**
   * The text of button used to cancel performing the action. This will usually be something like “Cancel” or “Later”.
   * This field is not required.
   */
  cancel_button_text?: string;
}

export interface FormBlockerElementOptionPickerElementOptionSelectable {
  /** An optional confirmation dialog to present to the user when this option is selected. */
  confirmation_dialog?: FormBlockerElementOptionPickerElementOptionConfirmationDialog;
}

export interface FormBlockerElementOptionPickerElementOptionUnselectable {
  /**
   * A message to be presented to the customer when they attempt to select an unselectable
   * option. This field is expected to be non-null.
   */
  message?: string;
}

/**
 * An informational text row which has information at both the left and right sides.
 * This element is ideal for displaying key/value paired information, and multiple detail
 * row elements are often stacked adjacent to each other.
 *
 * Optionally, a subtitle may be added as well, which will be displayed on the left side
 * below the title. An image may also be added and will be displayed to the left
 * of the title/subtitle if used.
 */
export interface FormBlockerElementDetailRowElement {
  /**
   * Text shown at the left of the row. Generally the key in a key/value pair. Can support
   * Markdown.
   */
  title?: string;
  /** Text shown at the right of the row. Generally the value in a key/value pair. Can Markdown. */
  detail_text?: string;
  /** Text shown at the left of the row, below the title. Can support Markdown. */
  subtitle?: string;
  /**
   * Action taken when the left side of the row is tapped.
   * Clients will automatically add an indicator that the left side is tappable.
   */
  title_action?: BlockerAction;
}

/**
 * An informational row with a title, subtitle, optional detail text beneath the subtitle,
 * and optional icon at the left side. May or may not have an action. If the action is set, the
 * row is tappable and we display a chevron icon on the right edge of the row. If the action is
 * empty, we don't show any chevron and the row isn't tappable.
 */
export interface FormBlockerElementSelectableRowElement {
  /** The title of the option. Displayed as bold text. */
  title?: string;
  /** Additional text to describe the option. Shown below the title. */
  subtitle?: string;
  /**
   * Used for call-to-action or an important detail (ex. fee). May be null.
   * Displayed in an accent color, unless detail_text_style is set.
   */
  detail_text?: string;
  /** Accessibility label for this option. */
  accessibility_text?: string;
  /**
   * Action taken when anywhere in the row is tapped. May be null if the row is not tappable.
   * DEPRECATED in favor of row_action below.
   *
   * @deprecated
   */
  action?: BlockerAction;
  /** Local icon at the left side of the row. May be null. */
  icon?: FormBlockerElementSelectableRowElementIcon;
  /** Used for client side styling of the cell. */
  appearance?: FormBlockerElementSelectableRowElementAppearance;
  /** Action taken when anywhere in the row is tapped. May be null if the row is not tappable. */
  row_action?: FormBlockerElementSelectableRowElementAction;
  /**
   * Remote image displayed at the left side of the row. May be null.
   * Will be rendered above `icon` if both are provided, once downloaded.
   * Available in client versions >= 3.53.
   */
  remote_image?: Image;
  /**
   * Styling for the detail_text. May be null.
   * Available in client versions >= 3.87.
   */
  detail_text_style?: FormBlockerElementSelectableRowElementDetailTextStyle;
}

export enum FormBlockerElementSelectableRowElementIcon {
  KEYPAD = "KEYPAD",
  CARD = "CARD",
  UP_AND_DOWN_ARROWS = "UP_AND_DOWN_ARROWS",
  LOCK = "LOCK",
  TRUCK = "TRUCK",
  UNCHECKED_BOX = "UNCHECKED_BOX",
  CHECKED_BOX = "CHECKED_BOX",
  LIGHTNING = "LIGHTNING",
  CALENDAR = "CALENDAR",
  INBOX = "INBOX",
  /** CHECKMARK - Available on client versions >= v3.27 */
  CHECKMARK = "CHECKMARK",
  EXCLAMATION_MARK = "EXCLAMATION_MARK",
  CLOCK = "CLOCK",
  DOLLAR = "DOLLAR",
  INFORMATION = "INFORMATION",
  NUMBER_ONE = "NUMBER_ONE",
  NUMBER_TWO = "NUMBER_TWO",
  NUMBER_THREE = "NUMBER_THREE",
  NUMBER_FOUR = "NUMBER_FOUR",
  NUMBER_FIVE = "NUMBER_FIVE",
  NUMBER_SIX = "NUMBER_SIX",
  NUMBER_SEVEN = "NUMBER_SEVEN",
  NUMBER_EIGHT = "NUMBER_EIGHT",
  NUMBER_NINE = "NUMBER_NINE",
  /** BANK - Available on client versions >= v3.38 */
  BANK = "BANK",
  SHIELD_CHECKMARK = "SHIELD_CHECKMARK",
  DOCUMENT = "DOCUMENT",
  PERSON = "PERSON",
  ENCIRCLED_PLUS = "ENCIRCLED_PLUS",
  /** GLOBE - Available on client versions >= v3.53 */
  GLOBE = "GLOBE",
  CONTACTLESS = "CONTACTLESS",
  /** LOCATION - Available on client version >= v3.54 */
  LOCATION = "LOCATION",
  MOBILE_DEVICE = "MOBILE_DEVICE",
  /** GIFT_CARD - Available in 3.60.+ */
  GIFT_CARD = "GIFT_CARD",
  /** PRICE_TAG - Available in 3.60.+ */
  PRICE_TAG = "PRICE_TAG",
  /** HEART - Available in 3.63.+ */
  HEART = "HEART",
  /** BULLET - Available in 3.65.+ */
  BULLET = "BULLET",
  FDIC_INSURED = "FDIC_INSURED",
  /** FAMILY - Available in 3.70.+ */
  FAMILY = "FAMILY",
  PRIVACY = "PRIVACY",
  UPPER_LIMIT = "UPPER_LIMIT",
  CASH_APP = "CASH_APP",
  /** INVESTING - Available in 3.71.+ */
  INVESTING = "INVESTING",
  CAPITOL = "CAPITOL",
  /** ZERO_CROSSED - Available in 3.74+ */
  ZERO_CROSSED = "ZERO_CROSSED",
  /** HAPPY_FACE - Available in 3.75+ */
  HAPPY_FACE = "HAPPY_FACE",
  /** SPEND_LOCATION - Available in 3.79+ */
  SPEND_LOCATION = "SPEND_LOCATION",
  CARD_BALANCE = "CARD_BALANCE",
  TAP_PAYMENT = "TAP_PAYMENT",
  NOTE = "NOTE",
  NEVER_EXPIRES = "NEVER_EXPIRES",
  /** DOCUMENTS_OUTLINE - Available in 3.81+ */
  DOCUMENTS_OUTLINE = "DOCUMENTS_OUTLINE",
  DOCUMENTS_FINANCIAL = "DOCUMENTS_FINANCIAL",
  DOCUMENTS_W2 = "DOCUMENTS_W2",
  DOCUMENTS_PAYSTUB = "DOCUMENTS_PAYSTUB",
  DOCUMENTS_WILL = "DOCUMENTS_WILL",
  DOCUMENTS_COURT_ORDER = "DOCUMENTS_COURT_ORDER",
  DOCUMENTS_CERTIFICATE = "DOCUMENTS_CERTIFICATE",
  /** GOVERNMENT_IDENTIFIER - Available in 3.85+ */
  GOVERNMENT_IDENTIFIER = "GOVERNMENT_IDENTIFIER",
  /** RECURRING_DOUBLE_ARROW - Available in 3.92+ */
  RECURRING_DOUBLE_ARROW = "RECURRING_DOUBLE_ARROW",
  /** FAVORITES - Available in 3.99+ */
  FAVORITES = "FAVORITES",
  CONTACTS = "CONTACTS",
  /**
   * PROHIBITED - TODO(nowusu) add the min version when it is known
   * Available in ?.??+
   */
  PROHIBITED = "PROHIBITED",
}

export enum FormBlockerElementSelectableRowElementAppearance {
  /** STANDARD - Icons are the default icon color without tint. */
  STANDARD = "STANDARD",
  /** DISABLED - Icons are tinted to appear disabled. */
  DISABLED = "DISABLED",
  /** TINTED - Icons are tinted to the accent color set in BlockerDescriptor's theme. Available in 3.63.+ */
  TINTED = "TINTED",
}

/** Optional styling for detail_text. */
export enum FormBlockerElementSelectableRowElementDetailTextStyle {
  /** DEFAULT - detail_text will be displayed in an accent color. */
  DEFAULT = "DEFAULT",
  /** CRITICAL - detail_text will be displayed in red. */
  CRITICAL = "CRITICAL",
}

/** Action taken when anywhere in the row is tapped. */
export interface FormBlockerElementSelectableRowElementAction {
  /**
   * Optional--if populated, the button will be a secondary button with text;
   * if empty, the button will be a chevron.
   */
  label?: string;
  action?: BlockerAction;
}

/**
 * A specific glyph which the client knows how to render.
 *
 * Icons should generally be something which isn't easily represented by a `RemoteImageElement`
 * and is not downloadable from the server. This usually implies that the icon is animated or
 * is necessary in an offline scenario.
 */
export interface FormBlockerElementLocalImageElement {
  icon?: FormBlockerElementLocalImageElementIcon;
}

export enum FormBlockerElementLocalImageElementIcon {
  /** INSTANT - Show an animated lightning bolt */
  INSTANT = "INSTANT",
  /** SUCCESS - Show an animated checkmark */
  SUCCESS = "SUCCESS",
  /** FAILURE - Show an X icon: (X) */
  FAILURE = "FAILURE",
  /** PENDING - Show an ellipsis icon: (•••) */
  PENDING = "PENDING",
  /** ACTION_REQUIRED - Show an exclamation point icon: ( ! ) */
  ACTION_REQUIRED = "ACTION_REQUIRED",
  /** LOCKED - Show a lock icon */
  LOCKED = "LOCKED",
  /** RECURRING - Show a recurring icon: ( ↻ ) */
  RECURRING = "RECURRING",
  /** BORROW - Show the instant borrow icon */
  BORROW = "BORROW",
  /** DEPOSIT - Show the deposit icon */
  DEPOSIT = "DEPOSIT",
  /**
   * VERIFICATION - Show the verification shield icon with a checkmark. This
   * corresponds to "Verified" in Mooncake Core
   */
  VERIFICATION = "VERIFICATION",
  /**
   * VERIFYING - Show the verification shield icon with a lock. This
   * corresponds to "Verifying" in Mooncake Core
   */
  VERIFYING = "VERIFYING",
  /** DOCUMENT - Show a document icon */
  DOCUMENT = "DOCUMENT",
  /** SCRIBBLE - Show a scribble icon */
  SCRIBBLE = "SCRIBBLE",
  /** QR_CODE - Show a QR Code icon. This corresponds to "QR Code" in Mooncake Core */
  QR_CODE = "QR_CODE",
  /** CVV - Show a CVV icon */
  CVV = "CVV",
  /** CARD_MAILER_HERO - Cash Card Mailer Hero Image. Full width image */
  CARD_MAILER_HERO = "CARD_MAILER_HERO",
  /** CARD_CVV_LOCATION_HERO - Cash Card CVV Location Hero Image. Full width image */
  CARD_CVV_LOCATION_HERO = "CARD_CVV_LOCATION_HERO",
  /** CARD - Show a Cash Card Icon */
  CARD = "CARD",
  /** BANK - Show a bank icon (Supported by iOS 3.27+ and Android 3.28+) */
  BANK = "BANK",
  /** SECURITY_WARNING - Show a filled shield icon with "!". Added in 3.30 */
  SECURITY_WARNING = "SECURITY_WARNING",
  /** CALENDAR - Show a calendar icon */
  CALENDAR = "CALENDAR",
  /** CHECK_DEPOSIT_ENDORSEMENT_HERO - Check Deposit Endorsement Hero Image. Full width image */
  CHECK_DEPOSIT_ENDORSEMENT_HERO = "CHECK_DEPOSIT_ENDORSEMENT_HERO",
  /** ENVELOPE - Show an envelope icon */
  ENVELOPE = "ENVELOPE",
  /** LOGOMARK - Show the Cash logo icon */
  LOGOMARK = "LOGOMARK",
  /** CAMERA - Show the Camera icon. Added in 3.46. */
  CAMERA = "CAMERA",
  /** VERIFICATION_FAILED - Show a filled shield icon with a "!" in a circle. Added in 3.60 */
  VERIFICATION_FAILED = "VERIFICATION_FAILED",
  /** INVESTING_ROUND_UPS - Used by Investing Round Ups * */
  INVESTING_ROUND_UPS = "INVESTING_ROUND_UPS",
  /** INVESTING_ROUND_UPS_INTRO_ILLUSTRATION - Used by Investing Round Ups for the illustration on its introduction screen * */
  INVESTING_ROUND_UPS_INTRO_ILLUSTRATION = "INVESTING_ROUND_UPS_INTRO_ILLUSTRATION",
  /** UNLOCK - Show the unlock icon. Added in 3.70 */
  UNLOCK = "UNLOCK",
  /** FAMILY - Show the family icon. Added in 3.70 */
  FAMILY = "FAMILY",
  /** PERSON - Show the person icon. Added in 3.70 */
  PERSON = "PERSON",
  /** INVESTING - Show the investing icon. */
  INVESTING = "INVESTING",
  /** GOAL - Show the goal icon. Used by Cash Banking Money Management */
  GOAL = "GOAL",
  /** WIRE - Show the wire transfer icon. Added in 3.82. */
  WIRE = "WIRE",
  /** GLOBE - Show the globe icon. Used by Remittances */
  GLOBE = "GLOBE",
  /** RECURRING_DOUBLE_ARROW - Show the recurring icon with two arrows. Used for Recurring Payments. */
  RECURRING_DOUBLE_ARROW = "RECURRING_DOUBLE_ARROW",
  /** CARD_TOP_RIGHT_PLUS - Show the cash card top right plus icon []+, Added in 3.92 */
  CARD_TOP_RIGHT_PLUS = "CARD_TOP_RIGHT_PLUS",
  /** CARD_CHECKMARK - Show the cash card with checkmark icon []V, Added in 3.92 */
  CARD_CHECKMARK = "CARD_CHECKMARK",
  /** CARD_EXPIRED - Show the cash card expired icon []x, Added in 3.92 */
  CARD_EXPIRED = "CARD_EXPIRED",
  /** LOCATION - Show the location icon, Added in 3.92 */
  LOCATION = "LOCATION",
  /** WARNING - Show the warning ⚠️ icon, Added in 3.92 */
  WARNING = "WARNING",
  /** DECLINED - Show the declined 🚫 icon, Added in 3.92 */
  DECLINED = "DECLINED",
  /** DIVIDEND_REINVESTMENT_HERO - Show the dividend reinvestment hero image. Added in 3.99 */
  DIVIDEND_REINVESTMENT_HERO = "DIVIDEND_REINVESTMENT_HERO",
}

/**
 * An image displayed at a specified size. The size is defined as screen-scale-independent
 * points (not pixels). Thus, an image with a width of 100 points would be displayed at 200
 * pixels on a 2x screen, and 300 points on a 3x screen.
 *
 * The image is downloaded from the given URL and cached at the time that the form blocker
 * is received. The image download should be considered part of the same network
 * transaction as the one which updates the scenario plan. Any errors encountered during
 * the download should be shown to the user as if they were encountered during the server
 * call which included the form blocker in its response.
 */
export interface FormBlockerElementRemoteImageElement {
  /** @deprecated */
  url?: string;
  /** The width at which to display the image in screen points. */
  width?: number;
  /** The height at which to display the image in screen points. */
  height?: number;
  /** The horizontal alignment of the image. */
  alignment?: FormBlockerElementRemoteImageElementHorizontalAlignment;
  image?: Image;
}

export enum FormBlockerElementRemoteImageElementHorizontalAlignment {
  /** START - In LTR locales this means left aligned. */
  START = "START",
  /** CENTER - Center align the image. */
  CENTER = "CENTER",
  /** END - In LTR locales this means right aligned. */
  END = "END",
}

/**
 * A monetary amount, displayed as text. The currency symbol should be displayed smaller
 * than the numeric value.
 */
export interface FormBlockerElementMoneyElement {
  amount?: Money;
}

/** Text, such as a title, summary, or footer. */
export interface FormBlockerElementTextElement {
  /** The text to display. This text may contain links specified as Markdown. */
  text?: string;
  localized_text?: LocalizableString;
  /** The size at which to display the text. */
  size?: FormBlockerElementTextElementSize;
  /** The horizontal alignment of the text. */
  alignment?: FormBlockerElementTextElementHorizontalAlignment;
  /**
   * Text color, if nil text will be displayed in default color.
   * Introduced in iOS 3.61
   */
  text_color?: FormBlockerElementTextElementTextColor;
}

export enum FormBlockerElementTextElementSize {
  /** SMALL - Appropriate for footers */
  SMALL = "SMALL",
  /** MEDIUM - Appropriate for informational/summary text in a form */
  MEDIUM = "MEDIUM",
  /** LARGE - Appropriate for title text at the top of a form */
  LARGE = "LARGE",
}

export enum FormBlockerElementTextElementHorizontalAlignment {
  /** START - In LTR locales this means left aligned. */
  START = "START",
  /** CENTER - Center align the content. */
  CENTER = "CENTER",
  /** END - In LTR locales this means right aligned. */
  END = "END",
}

export enum FormBlockerElementTextElementTextColor {
  DEFAULT = "DEFAULT",
  /** WARNING - Red */
  WARNING = "WARNING",
}

/**
 * A flexible amount of vertical space.
 *
 * A `SpacerElement` expands to fill all of the vertical space remaining on the screen
 * after adding the other content elements.
 *
 * If multiple `SpacerElement`s are specified in the `elements` array then they each
 * consume a portion of the open vertical space as specified by the `proportion` field.
 * For example, if two `SpacerElement`s are specified, each with a proportion of `1`
 * (or any value so long as they're equal), then each spacer will consume half of the
 * available vertical empty space. If the two elements had `proportion`s of `1` and `2`,
 * then the latter spacer would be twice as tall as the former.
 */
export interface FormBlockerElementSpacerElement {
  proportion?: number;
}

/** Displays a Cash Card, including the customization. */
export interface FormBlockerElementCustomizedCardElement {
  card_customization?: CardCustomizationData;
}

/**
 * Represents an element with an animation, title, and subtitle, each of which are optional.
 * The elements should be vertically stacked in the aforementioned order.
 * We may opt to add support for an image in the hero in the future.
 * That would be a good time to add support for an `AnimationType` parameter to
 * allow for disambiguation.
 */
export interface FormBlockerElementHeroElement {
  /** The URL and the size(in points) of the animation. */
  animation?: Animation;
  /** The string to display for the title. Shown underneath the hero animation. */
  title?: string;
  /** The string to display for the subtitle. Shown underneath the title. */
  subtitle?: string;
  /**
   * The percentage of the form's total width to use, discounting form margins.
   * The height should be calculated by:
   * (form width * animation_width_percentage) * animation.aspectRatio
   */
  animation_width_percentage?: number;
}

/**
 * Displays a timeline. A vertical set of events connected by a line with information shown at
 * each event.
 */
export interface FormBlockerElementTimelineElement {
  events?: FormBlockerElementTimelineElementEvent[];
}

export interface FormBlockerElementTimelineElementEvent {
  icon?: FormBlockerElementTimelineElementEventIcon;
  /** Shown immediately beside the icon. Ex: Date. */
  title?: string;
  /**
   * Additional information shown beside the title (right aligned). Ex: Amount.
   * This field is optional.
   */
  detail_text?: string;
  /**
   * Information to further describe the title text. Shown in different format
   *  (Ex: lighter text color) directly after title text.
   */
  inline_description_text?: string;
  /** Used to style inline_description_text. If null, text format will match title. */
  inline_description_text_format?: FormBlockerElementTimelineElementEventInlineTextFormat;
}

export enum FormBlockerElementTimelineElementEventIcon {
  /** NORMAL - The default state. Usually used for an item in the future. */
  NORMAL = "NORMAL",
  /**
   * HIGHLIGHT - Used to draw focus to an event, such as by drawing the ring in an accent color.
   * Ex: Indicating the next loan payment.
   */
  HIGHLIGHT = "HIGHLIGHT",
  /**
   * ALERT - Used to draw attention to an event that requires immediate action, such as by drawing
   * the ring in red. Ex: An overdue loan payment.
   */
  ALERT = "ALERT",
  /** COMPLETED - Indicates that the event was successful, such as by showing a check mark. */
  COMPLETED = "COMPLETED",
  /** SKIPPED - Indicates that the event was skipped, such as by showing '>>'. */
  SKIPPED = "SKIPPED",
  /** MISSED - Indicates that the event was missed, such as by showing an 'x'. */
  MISSED = "MISSED",
  /**
   * HIGHLIGHT_COMPLETED - Used to draw focus to an event that was successful, such as by drawing the ring
   * in an accent color behind a check mark icon.
   * Available on Android clients 3.62+.
   */
  HIGHLIGHT_COMPLETED = "HIGHLIGHT_COMPLETED",
  /** FAILED - Indicates that the event has failed, such as by showing an 'x' with a red background. */
  FAILED = "FAILED",
  /** CANCELED - The event has been canceled. Available on 3.96+. */
  CANCELED = "CANCELED",
  /** REFUNDED - The event represents a refunded payment. Available on 3.96+. */
  REFUNDED = "REFUNDED",
}

export enum FormBlockerElementTimelineElementEventInlineTextFormat {
  /** PRIMARY - Matches title text. */
  PRIMARY = "PRIMARY",
  /** SECONDARY - Used to not draw primary focus away from title. */
  SECONDARY = "SECONDARY",
}

/** Displays text that is suitable for legal disclaimers, opt-out messaging or other legalese. */
export interface FormBlockerElementLegalTextElement {
  /** Markdown legal text to display as part of this element. */
  text?: string;
}

/**
 * Represents an element with title, subtitle, and button, along with optional
 * color for tint of button. This element is shown with a surrounding border
 * to make it clear that it is single cohesive element.
 */
export interface FormBlockerElementCallToActionElement {
  /**
   * Title of the element. May be null if subtitle is not null.
   * Supports Markdown starting 3.72+.
   */
  title?: string;
  /**
   * Subtitle of the element. May be null if title is not null.
   * Supports Markdown starting 3.72+.
   */
  subtitle?: string;
  /** Image to display above the title. May be null. */
  image?: Image;
  /**
   * NOT IMPLEMENTED IN IOS OR ANDROID YET.
   * The width at which to display the image in screen points.
   * If the specified width is less than or equal to the available width based on the screen size, then the image is displayed at the specified width and height, centered in the space.
   * If the specified width is greater than the available width, then the image is scaled down to match the available width, with the height being scaled down proportionally.
   */
  image_width?: number;
  /**
   * NOT IMPLEMENTED IN IOS OR ANDROID YET.
   * The height at which to display the image in screen points.
   * It will be scaled down when the specified width is greater than the available width. See more details at the "image_width" field.
   */
  image_height?: number;
  /**
   * The action for the button displayed at the bottom of the element.
   * May be null in which case no button will be shown.
   */
  button_action?: BlockerAction;
  /**
   * The accent color used to tint the button specified by button_action, if any.
   * If null, the button will use the accent color specified in the blocker descriptor's theme.
   */
  accent_color?: Color;
}

/**
 * An element displaying an investing tile. The tile will be horizontally aligned and will
 * consist of a remote icon and a caption. The tile's background color is also defined.
 * Clients are to add a graph image to the background of the tile, between the icon and the
 * caption.
 * For example: https://www.figma.com/file/1GPhh2imz3ukEGxFmLnI0U/Stock%2FBTC-Gifting?node-id=326%3A41164
 */
export interface FormBlockerElementCaptionedTileElement {
  icon?: Image;
  /** The accent color to use as the background of the element. */
  background_color?: Color;
  caption?: string;
  accessibility_text?: string;
}

/**
 * An element containing a gift card.
 * Encompasses the card itself as well as the title presented above it and the caption
 * presented below it.
 * For example: https://www.figma.com/file/C2qjjbKQ4ojevq9Q64P95K/Gift-Cards?node-id=1186%3A44796
 */
export interface FormBlockerElementGiftCardElement {
  /** The message attached to the gift card by the sender. */
  note?: string;
  /**
   * Deprecated in favor of "card" which has the company logo on a color background as
   * one image.
   */
  card_logo?: Image;
  /** The amount the gift card is for, formatted for display. */
  amount_text?: string;
  /**
   * An informative caption about this card, set by Cash App business logic, not
   * the gift card sender.
   */
  caption_text?: string;
  /** Accessibility label for the Gift Card. */
  accessibility_label?: string;
  /**
   * The company logo on top of a color background as one image. Clients are responsible
   * for rounding image corners if required.
   */
  card?: Image;
}

/** For cases where the card is shown outside of a badged view */
export interface FormBlockerElementCardElement {
  /** The remote image. Clients will round corners of this image. */
  image?: Image;
  /** The width to display in points. */
  width?: number;
  /** The height to display in points. */
  height?: number;
}

/**
 * A group of vertically stacked or a single element that can
 * be copied to a user's clipboard.
 */
export interface FormBlockerElementCopyableElementGroup {
  elements?: FormBlockerElementCopyableElementGroupCopyableElement[];
}

export interface FormBlockerElementCopyableElementGroupCopyableElement {
  /**
   * The value to be copied, e.g. an account number,
   * phone number, etc. without formatting applied.
   * An example is 2155551234 as a phone number.
   */
  value?: string;
  /**
   * The formatted value to display, e.g. a phone number as
   * (215) 555-1234, which is more readable, but potentially
   * less usable when copied. This is optional if no formatting is required.
   */
  formatted_value?: string;
  /** The label, e.g. "Phone number." */
  label?: string;
  /**
   * The accessibility text when selected, e.g.
   * "Click to copy the phone number of 2155551234."
   */
  accessibility_text?: string;
}

/** Intended to highlight a piece of information for the customer */
export interface FormBlockerElementNoticeElement {
  /** The body of the notice */
  text?: string;
  /** The icon displayed alongside the notice */
  icon?: FormBlockerElementNoticeElementIcon;
}

export enum FormBlockerElementNoticeElementIcon {
  PADLOCK = "PADLOCK",
}

/** Element for choosing an emoji from a specified list. */
export interface FormBlockerElementEmojiPickerElement {
  categories?: FormBlockerElementEmojiPickerElementEmojiCategory[];
  initial_selection?: FormBlockerElementEmojiPickerElementInitialSelection;
}

export interface FormBlockerElementEmojiPickerElementEmojiCategory {
  /** @deprecated */
  options?: string[];
  emojiOptions?: FormBlockerElementEmojiPickerElementEmojiCategoryEmojiOption[];
}

/**
 * The options likely each have a uniform unicode scalar prefix,
 * but we don't want to be strictly constrained to this setup.
 * So, we will repeat the full string for each option.
 *
 * We're anticipating a situation like a "family" emoji category
 * in the picker that we would like to provide a wide range for,
 * some of which start with a different scalar. Example:
 *
 * U+1F468 U+200D U+1F468 U+200D U+1F467 :family_man_man_girl:
 * U+1F469 U+200D U+1F469 U+200D U+1F467 :family_woman_woman_girl:
 *
 * In the initial implementation of the emoji picker, we'll have
 * only one option per category.
 */
export interface FormBlockerElementEmojiPickerElementEmojiCategoryEmojiOption {
  unicodeEmoji?: string;
  accessibilityText?: string;
}

/**
 * An index path specifying the initially selected emoji category
 * and variant within
 */
export interface FormBlockerElementEmojiPickerElementInitialSelection {
  categories_index?: number;
  options_index?: number;
}

/**
 * THIS ELEMENT WILL BE DEPRECATED. Please use UpsellElement instead.
 *
 * Intended to display family upsell contents. An example:
 * https://www.figma.com/file/5jucIPYCk9oMuSKgR2ydFV/%F0%9F%91%A8%E2%80%8D%F0%9F%91%A9%E2%80%8D%F0%9F%91%A7%E2%80%8D%F0%9F%91%A6-Families-v.2-Stocks%26Bitcoin?node-id=2430%3A272437
 * Supported in 3.74+ (due to image sizing bugs in 3.73)
 */
export interface FormBlockerElementFamilyUpsellElement {
  /** Title of the element. May be null if subtitle is not null. */
  title?: string;
  /** Subtitle of the element. May be null if title is not null. */
  subtitle?: string;
  /**
   * The width at which to display the image in screen points.
   * If the specified width is less than or equal to the available width based on the screen size, then the image is displayed at the specified width and height, centered in the space.
   * If the specified width is greater than the available width, then the image is scaled down to match the available width, with the height being scaled down proportionally.
   */
  image_width?: number;
  /**
   * The height at which to display the image in screen points.
   * It will be scaled down when the specified width is greater than the available width. See more details at the "image_width" field.
   */
  image_height?: number;
  /** Image to display above the title. May be null. */
  image?: Image;
}

/**
 * Intended to display upsell contents (originally known as FamilyUpsellElement).
 * Supported in Android 3.93+ and iOS 3.92+.
 */
export interface FormBlockerElementUpsellElement {
  /** Title of the element. May be null if subtitle is not null. */
  title?: string;
  /** Subtitle of the element. May be null if title is not null. */
  subtitle?: string;
  /**
   * The width at which to display the image in screen points.
   * If the specified width is less than or equal to the available width based on the screen size, then the image is displayed at the specified width and height, centered in the space.
   * If the specified width is greater than the available width, then the image is scaled down to match the available width, with the height being scaled down proportionally.
   * May be null if image is null.
   */
  image_width?: number;
  /**
   * The height at which to display the image in screen points.
   * It will be scaled down when the specified width is greater than the available width. See more details at the "image_width" field.
   * May be null if image is null.
   */
  image_height?: number;
  /** Image to display above the title. May be null. */
  image?: Image;
  /**
   * The action for the button displayed at the bottom of the element.
   * May be null in which case no button will be shown.
   *
   * Supported in client versions TBD (TODO: kma)
   */
  button_action?: BlockerAction;
}

export interface FormBlockerElementMerchantTransactionElement {
  /** The unique identifier of the transaction. This will be returned when used in option picker */
  transactionId?: string;
  /** Determine whether or not this selectable and should render selectable layout */
  selectable?: boolean;
  /** The merchant's avatar urls */
  avatar?: Avatar;
  /** Determine whether or not to show the shield badge on the avatar */
  avatarBadged?: boolean;
  /** The formatted amount of money spent at the merchant */
  amount?: FormBlockerElementMerchantTransactionElementAmount;
  /**
   * Pre formatted date information shown verbatim on client
   * E.g. "Sept 20th @ 9:30pm" or "Yesterday"
   */
  date?: string;
  /**
   * The first of optionally 2 lines describing the location of the purchase
   * E.g. "Modesto, CA" or "Online"
   */
  locationLineOne?: string;
  /**
   * The second of 2 lines describing the location of the purchase
   * E.g. "In-person"
   */
  locationLineTwo?: string;
  /**
   * The text used to describe the status of the payment.
   * E.g. "Declined"
   */
  transactionStatus?: string;
  /**
   * The color being applied to the transactionStatus text
   * E.g. red
   */
  transactionStatusColor?: Color;
  /**
   * The name of the merchant that the transaction was with
   * E.g. Starbucks
   */
  merchantName?: string;
}

export interface FormBlockerElementMerchantTransactionElementAmount {
  /** The amount of Money spent at the merchant */
  money?: Money;
  /**
   * The markdown applied to the amount text.
   * Only provide markdown for one side of the text and client will apply to both sides
   * E.g "~~" for strikethrough text
   */
  markdown?: string;
}

export interface FormBlockerElementMerchantTransactionOptionPicker {
  options?: FormBlockerElementMerchantTransactionElement[];
}

/**
 * An outlined box containing a header and detail row elements.
 * https://www.figma.com/file/WD3IBrAOCQrOR6XzTY39Di/%F0%9F%9A%A7---Payment-Plans-(fka-Retro-Financing)?node-id=2285%3A204823&t=qhmn4qzT4Xw1EiwB-4
 */
export interface FormBlockerElementPaymentPlanSummaryElement {
  /** The header, not optional. */
  header?: string;
  /** The summary item rows, minimum 1. */
  items?: FormBlockerElementDetailRowElement[];
}

/** Summarizes an upcoming payment schedule using circular progress items. */
export interface FormBlockerElementPaymentPlanScheduleElement {
  /** The header text. Not optional. */
  header?: string;
  /** Body text to show beneath the header. Optional. */
  body?: string;
  /** Array of progress element objects. May be empty. */
  circle_progress_items?: FormBlockerElementPaymentPlanScheduleElementCircleProgressItem[];
  /** Icon representing the source of the scheduled payments. Optional. */
  source_icon?: Image;
  /** Text describing the source of the scheduled payments. Supports markdown. Optional. */
  source_text?: string;
  /** Action to take when the source info section is clicked. */
  source_info_click_action?: BlockerAction;
}

/** A circle with amount progress cleared, a larger title below the circle, and subtitle below. */
export interface FormBlockerElementPaymentPlanScheduleElementCircleProgressItem {
  /**
   * How much to fill in the circles outer ring. Not optional.
   * 0.0 = 0%, 1.0 = 100%
   */
  progress_percent?: number;
  /** The label that appears inside the circle. One character. Optional. */
  circle_label?: string;
  /** Bolded text that appears under the circle. Optional. */
  primary_text?: string;
  /** Not bolded text that appears under primary text. Optional. */
  secondary_text?: string;
}

/**
 * A field to input a location without specific address. It will return
 * neighborhood, locality and administrative_area_1 on GlobalAddress.
 */
export interface FormBlockerElementLocationElement {
  /** The initial value with which to populate the address fields. Optional. */
  prefill_address?: GlobalAddress;
  /** The country where the expected address is located. Optional. */
  country_code?: Country;
}

export interface FormBlockerRemoteOnDisplayEffect {
  animation?: Animation;
}
